const BASE_URL = process.env.REACT_APP_URL_INFO;

// CACHE UTILITIES
const cache = {
  geographicUnits: new Map(),
  placeIndicatorPairs: new Map(),
  filteredPairs: new Map(),
  housePrices: new Map(),
  indicatorSeries: new Map(),
  indicatorRanges: new Map(),
  suburbIndicatorTable: new Map(),
  suburbLookup: new Map(),
  latestBySuburb: new Map(),
};

function getCached(cacheMap, key) {
  return cacheMap.get(key);
}

function setCached(cacheMap, key, value) {
  cacheMap.set(key, value);
}

// GEOGRAPHIC UNITS
export async function fetchGeographicUnits(level) {
  const key = level;
  const cached = getCached(cache.geographicUnits, key);
  if (cached) return cached;

  const res = await fetch(`${BASE_URL}/geographic-unit/by-level?level=${level}`);
  const raw = await res.json();
  const data = raw.flatMap(group => group.data || []);

  setCached(cache.geographicUnits, key, data);
  return data;
}

export async function fetchGeographicUnitGroups(level) {
  const key = `GROUPS_${level}`;
  const cached = getCached(cache.geographicUnits, key);
  if (cached) return cached;

  const res = await fetch(`${BASE_URL}/geographic-unit/by-level?level=${level}`);
  const raw = await res.json();

  setCached(cache.geographicUnits, key, raw);
  return raw;
}

// INDICATOR MAPPINGS
export async function fetchPlaceIndicatorPairs(level = "State") {
  const key = level;
  const cached = getCached(cache.placeIndicatorPairs, key);
  if (cached) return cached;

  const res = await fetch(`${BASE_URL}/geographic-unit/by-level?level=${level}`);
  const raw = await res.json();
  const placeNames = raw.flatMap(group => group.data || []);

  const pairs = await Promise.all(
    placeNames.map(async (placeName) => {
      const dataRes = await fetch(`${BASE_URL}/indicator-data/by-place?place=${encodeURIComponent(placeName)}`);
      const data = await dataRes.json();

      const placeId = data.geoUnitId;
      const indicatorId = data.data?.[0]?.indicatorName || null;

      if (!placeId || !indicatorId) return null;
      return { placeId, indicatorId };
    })
  );

  const validPairs = pairs.filter(Boolean);
  setCached(cache.placeIndicatorPairs, key, validPairs);
  return validPairs;
}

export async function fetchIndicatorPairsByPlaceIds(targetPlaceIds = [], level = "State") {
  const key = JSON.stringify({ targetPlaceIds, level });
  const cached = getCached(cache.filteredPairs, key);
  if (cached) return cached;

  const res = await fetch(`${BASE_URL}/geographic-unit/by-level?level=${level}`);
  const raw = await res.json();
  const placeNames = raw.flatMap(group => group.data || []);

  const pairs = await Promise.all(
    placeNames.map(async (placeName) => {
      const dataRes = await fetch(`${BASE_URL}/indicator-data/by-place?place=${encodeURIComponent(placeName)}`);
      const data = await dataRes.json();

      const placeId = data.geoUnitId;
      const indicatorId = data.data?.[0]?.indicatorName || null;

      if (!placeId || !indicatorId) return null;
      if (!targetPlaceIds.includes(placeId)) return null;

      return { placeId, indicatorId };
    })
  );

  const validPairs = pairs.filter(Boolean);
  setCached(cache.filteredPairs, key, validPairs);
  return validPairs;
}

// HOUSE PRICES
export async function fetchHousePricesByPlaceAndIndicator(pairs) {
  const key = JSON.stringify(pairs);
  const cached = getCached(cache.housePrices, key);
  if (cached) return cached;

  const results = {};

  await Promise.all(
    pairs.map(async ({ placeId, indicatorId }) => {
      try {
        const res = await fetch(
          `${BASE_URL}/indicator-data/ind-place?ind=${indicatorId}&place=${placeId}`
        );
        const data = await res.json();
        results[placeId] = data;
      } catch {
        results[placeId] = [];
      }
    })
  );

  setCached(cache.housePrices, key, results);
  return results;
}

// INDICATOR SERIES
export async function fetchIndicatorSeries(indicatorId, placeIds = [], years = []) {
  const key = JSON.stringify({ indicatorId, placeIds, years });
  const cached = getCached(cache.indicatorSeries, key);
  if (cached) return cached;

  const results = {};

  await Promise.all(
    placeIds.map(async (placeId) => {
      const series = [];

      for (const year of years) {
        try {
          const res = await fetch(
            `${BASE_URL}/geographic-unit/snipped-average?ind=${indicatorId}&place=${placeId}&year=${year}`
          );
          const json = await res.json();

          const value =
            typeof json.value === "number"
              ? json.value
              : typeof json.average === "number"
              ? json.average
              : null;

          series.push({ date: `${year}`, value });
        } catch {
          series.push({ date: `${year}`, value: null });
        }
      }

      results[placeId] = series;
    })
  );

  setCached(cache.indicatorSeries, key, results);
  return results;
}

// REFRESH HELPERS
export async function refreshHousePrices(pairs) {
  clearCacheByCategory("housePrices");
  return await fetchHousePricesByPlaceAndIndicator(pairs);
}

export async function refreshIndicatorSeries(indicatorId, placeIds, years) {
  clearCacheByCategory("indicatorSeries");
  return await fetchIndicatorSeries(indicatorId, placeIds, years);
}

// TOP SUBURBS BY INDICATOR (con secundario)
export async function fetchTopSuburbsByIndicator({ city, indicator, secondary, order = 'desc', limit = 10 }) {
  const params = new URLSearchParams();
  if (city) params.set('city', city);
  if (indicator) params.set('indicator', indicator);
  if (secondary) params.set('secondary', secondary);
  if (order) params.set('order', order);
  if (Number.isFinite(limit)) params.set('limit', String(limit));

  const res = await fetch(`${process.env.REACT_APP_URL_INFO}/indicator-data/top-suburbs?${params.toString()}`);
  if (!res.ok) throw new Error(`Top suburbs failed: ${res.status}`);

  const json = await res.json();

  const raw = Array.isArray(json) ? json
           : Array.isArray(json?.data) ? json.data
           : Array.isArray(json?.items) ? json.items
           : [];

  return raw.map(r => ({
    city: r.city ?? city,
    suburb: r.suburb ?? r.name ?? '',
    value: (typeof r.value === 'number') ? r.value : (r.value != null ? Number(r.value) : null),
    secondaryValue: (typeof r.secondaryValue === 'number')
      ? r.secondaryValue
      : (r.secondaryValue != null ? Number(r.secondaryValue) : null),
  }));
}

// MIX-MAX VALUE BY CITY
export async function fetchIndicatorRangesByCity(city, level = "Suburb") {
  if (!city) throw new Error("city is required");

  const key = JSON.stringify({ city, level });
  if (cache.indicatorRanges.has(key)) return cache.indicatorRanges.get(key);

  const params = new URLSearchParams();
  params.set("city", city);
  if (level) params.set("level", level);

  const url = `${BASE_URL}/indicator-data/ranges/by-city?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`ranges/by-city failed: ${res.status}`);

  const json = await res.json();
  const ranges = json?.ranges && typeof json.ranges === "object" ? json.ranges : {};

  cache.indicatorRanges.set(key, ranges);
  return ranges;
}

// SUBURB INDICATORS TABLE BY CITY
function toNum(x) {
  if (typeof x === "number") return x;
  if (x == null) return null;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

export async function fetchSuburbIndicatorsTableByCity(city) {
  if (!city) throw new Error("city is required");

  const key = `TABLE_${city}`;
  const cached = getCached(cache.suburbIndicatorTable, key);
  if (cached) return cached;

  const params = new URLSearchParams({ city });
  const url = `${BASE_URL}/indicator-data/table/by-city?${params.toString()}`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`table/by-city failed: ${res.status}`);

  const json = await res.json();
  const rowsRaw = Array.isArray(json)
    ? json
    : Array.isArray(json?.data)
    ? json.data
    : Array.isArray(json?.items)
    ? json.items
    : [];

  const rows = rowsRaw.map((r) => ({
    suburb: r.suburb ?? r.name ?? "",
    median12m: toNum(r.median12m ?? r.median_sales_price_12m ?? r.medianSalesPrice12Months),
    median5y: toNum(r.median5y),
    median10y: toNum(r.median10y),
    median20y: toNum(r.median20y),
    ltgrowth20ypct: toNum(r.ltgrowth20ypct ?? r.lt_growth_rate_20y_pct),
    pct5y: toNum(r.pct5y ?? r.median_price_5y_pct_change),
    pct10y: toNum(r.pct10y ?? r.median_price_10y_pct_change),
    pct20y: toNum(r.pct20y ?? r.median_price_20y_pct_change),
    rentalyield12mpct: toNum(r.rentalyield12mpct ?? r.median_rental_yield_12m_pct),
  }));

  setCached(cache.suburbIndicatorTable, key, rows);
  return rows;
}

export const infoApi = {
  async getSuburbIndicatorRowsByCity(city) {
    const rows = await fetchSuburbIndicatorsTableByCity(city);
    return { data: rows };
  },
  async getSuburbLookup(city, suburb) {
    return await fetchSuburbLookup(city, suburb);
  },
  async getLatestBySuburb(city, suburb, indicators) {
    return await fetchLatestIndicatorsBySuburb(city, suburb, indicators);
  },
};

// SUBURB LOOKUP
export async function fetchSuburbLookup(city, suburb) {
  if (!city || !suburb) throw new Error("city and suburb are required");
  const key = `${city}::${suburb}`;
  const cached = cache.suburbLookup.get(key);
  if (cached) return cached;

  const params = new URLSearchParams({ city, name: suburb });
  const res = await fetch(`${BASE_URL}/geographic-unit/lookup?${params.toString()}`);
  if (!res.ok) throw new Error(`lookup failed: ${res.status}`);

  const json = await res.json();
  const d = json?.data ?? json ?? {};

  const normalized = {
    city: d.parentName ?? city ?? null,
    suburb: d.name ?? suburb ?? null,
    postCode: d.postCode ?? d.postalCode ?? null,
    lat: d.lat ?? d.latitude ?? null,
    lon: d.lon ?? d.longitude ?? null,
    geoUnitId: d.geoUnitId ?? d.id ?? null,
    level: d.level ?? "Suburb",
  };

  cache.suburbLookup.set(key, normalized);
  return normalized;
}

// LATEST INDICATORS BY SUBURB
export async function fetchLatestIndicatorsBySuburb(city, suburb, indicators = []) {
  if (!city || !suburb) throw new Error("city and suburb are required");
  const inds = (indicators && indicators.length)
    ? indicators.map(s => String(s).trim()).filter(Boolean)
    : ["vacancy_rate_pct","demand_rea_index","houses_available","sales_count_12_months"];

  const key = JSON.stringify({ city, suburb, inds: inds.slice().sort() });
  const cached = cache.latestBySuburb.get(key);
  if (cached) return cached;

  const params = new URLSearchParams({ city, suburb, indicators: inds.join(",") });
  const res = await fetch(`${BASE_URL}/indicator-data/latest/by-suburb?${params.toString()}`);
  if (!res.ok) throw new Error(`latest/by-suburb failed: ${res.status}`);

  const json = await res.json();
  const raw = json?.data && typeof json.data === "object" ? json.data : {};

  const normalized = {};
  for (const [k, v] of Object.entries(raw)) {
    normalized[k] = {
      value: typeof v?.value === "number" ? v.value : (v?.value != null ? Number(v.value) : null),
      unit: v?.unit ?? null,
      period: v?.period ?? null,
    };
  }

  cache.latestBySuburb.set(key, normalized);
  return normalized;
}

// CACHE CONTROL UTILS
export function clearAllCaches() {
  Object.values(cache).forEach(cacheMap => cacheMap.clear());
}

export function clearCacheByCategory(category) {
  if (cache[category]) {
    cache[category].clear();
  }
}

// INDICATOR TABLE CLEAR
export function clearSuburbIndicatorsTableCache() {
  cache.suburbIndicatorTable.clear();
}

// HELPERS
export function clearIndicatorRangesCache() {
  cache.indicatorRanges.clear();
}